import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, BadgeCheck } from "lucide-react";

const empty = { full_name: "", title: "", email: "", phone: "", password: "", confirm: "", notes: "", declaration: false };

export function ClaimSubmitDialog({ facility, open, onOpenChange }) {
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (open) { setForm(empty); setSubmitted(false); } }, [open, facility?.facility_id]);

  const handleClose = (v) => { onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.declaration) { toast.error("Please accept the declaration"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/claim", {
        facility_id: facility.facility_id,
        full_name: form.full_name,
        title: form.title,
        email: form.email,
        phone: form.phone,
        password: form.password,
        declaration: form.declaration,
        notes: form.notes,
      });
      if (data.account_created) { try { await refresh(); } catch (err) { console.error("Refresh after claim failed", err); } }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit claim");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="claim-submit-dialog">
        {submitted ? (
          <div data-testid="claim-submit-success" className="text-center">
            <DialogHeader>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#E3F3ED] flex items-center justify-center mb-2">
                <ShieldCheck className="h-8 w-8 text-[#0077B6]" />
              </div>
              <DialogTitle className="font-heading text-2xl text-center">Thanks! We're verifying your request</DialogTitle>
              <DialogDescription className="text-center">
                We've received your request to claim <b>{facility?.name}</b> and will email you at <b>{form.email}</b> within 1 business day.
              </DialogDescription>
            </DialogHeader>
            <Button onClick={() => handleClose(false)} data-testid="claim-submit-done" className="mt-6 bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-2"><BadgeCheck className="h-6 w-6 text-[#0077B6]" /> Claim your listing</DialogTitle>
              <DialogDescription>Create your director account and submit your claim in one step. We verify every request.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="claim-facility-name">Facility</Label>
                <Input id="claim-facility-name" data-testid="claim-facility-name" value={facility?.name || ""} readOnly className="bg-[#F7F6F2] cursor-default" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label htmlFor="claim-full-name">Full name *</Label><Input id="claim-full-name" required data-testid="claim-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
                <div className="space-y-1.5"><Label htmlFor="claim-job-title">Job title *</Label><Input id="claim-job-title" required data-testid="claim-job-title" placeholder="Administrator" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label htmlFor="claim-work-email">Work email *</Label><Input id="claim-work-email" required type="email" data-testid="claim-work-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div className="space-y-1.5"><Label htmlFor="claim-work-phone">Work phone *</Label><Input id="claim-work-phone" required data-testid="claim-work-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label htmlFor="claim-password">Password *</Label><Input id="claim-password" required type="password" data-testid="claim-password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" /></div>
                <div className="space-y-1.5"><Label htmlFor="claim-confirm-password">Confirm password *</Label><Input id="claim-confirm-password" required type="password" data-testid="claim-confirm-password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="claim-notes">Anything else we should know?</Label><Textarea id="claim-notes" rows={2} data-testid="claim-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
              <label className="flex items-start gap-2.5 cursor-pointer bg-[#F7F6F2] rounded-xl p-3 border border-[#E8E6DF]">
                <Checkbox checked={form.declaration} onCheckedChange={(v) => set("declaration", !!v)} className="mt-0.5" data-testid="claim-declaration-check" />
                <span className="text-xs leading-relaxed text-[#4A534E]">
                  I declare, under penalty of perjury, that I am authorized to represent and manage this facility's listing and that all information I have provided is true and accurate.
                </span>
              </label>
              <Button type="submit" disabled={loading || !form.declaration} data-testid="claim-submit-request" className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Claim Request"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
